import { ChevronRight, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SettingsCategoryList } from "@/domains/settings/components/settings-category-list";
import { useIsMobile } from "@/hooks/hooks/use-mobile";

interface SettingsNavigationProps {
  variant?: "salon" | "settings";
  activeSection?: string;
}

export function SettingsNavigation({ variant = "salon" }: SettingsNavigationProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation("settings");

  if (variant !== "salon") return null;

  // Mobile: collapse all categories behind a single drill-in entry. Tap
  // navigates to /settings which renders the full categories list. Keeps the
  // salon page short and matches the native mobile pattern.
  if (isMobile) {
    return (
      <Button
        variant="ghost"
        onClick={() => navigate("/settings")}
        className="h-auto p-3 justify-start hover:bg-muted/50 group w-full"
      >
        <div className="flex items-center space-x-4 w-full">
          <div className="flex-shrink-0">
            <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center group-hover:bg-muted/80 transition-colors">
              <Settings className="w-5 h-5 text-muted-foreground" />
            </div>
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-base font-medium text-foreground">
              {t("settingsAndConfiguration", "Settings & Configuration")}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground" />
        </div>
      </Button>
    );
  }

  // Desktop keeps the expanded view inline so the user can scan categories
  // without an extra navigation step.
  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-6">
        {t("settingsAndConfiguration", "Settings & Configuration")}
      </h3>
      <SettingsCategoryList />
    </div>
  );
}
